import VoicemailService from '@/services/VoicemailService'

function safeArray(v) {
  if (Array.isArray(v)) return v
  if (Array.isArray(v?.data)) return v.data
  return []
}

function parseCallerId(callerid) {
  const raw = String(callerid ?? '').trim()
  if (!raw) return { fromName: '', fromNumber: '' }

  // common pattern: "NAME" <+15551234567>
  const m = raw.match(/"([^"]+)"\s*<([^>]+)>/)
  if (m) return { fromName: m[1] || '', fromNumber: m[2] || '' }

  // fallback: <number>
  const n = raw.match(/<([^>]+)>/)
  if (n) return { fromName: '', fromNumber: n[1] || '' }

  return { fromName: '', fromNumber: raw }
}

function epochToIso(origtime) {
  const t = String(origtime ?? '').trim()
  if (!t) return ''
  const num = Number(t)
  if (!Number.isFinite(num)) return ''
  // Asterisk origtime here looks epoch seconds
  const d = new Date(num * 1000)
  return d.toISOString()
}

const voicemail = {
  namespaced: true,

  state: () => ({
    mailboxes: [],
    loadingMailboxes: false,
    mailboxesError: '',

    currentMailboxId: null,

    messages: [],
    messagesPagination: {
      current_page: 1,
      last_page: 1,
      per_page: 50,
      total: 0
    },
    loadingMessages: false,
    messagesError: '',

    // filters for messages
    msgQuery: '',
    msgStatus: '', // '', 'read', 'unread'
    msgSort: '-id',

    // audio url cache per message id
    audioUrls: {},

    // user unread count (for badge)
    userUnreadCount: 0
  }),

  getters: {
    currentMailbox(state) {
      return state.mailboxes.find(m => String(m.uniqueid) === String(state.currentMailboxId)) || null
    },

    // normalized mailbox list for UI
    uiMailboxes(state) {
      return state.mailboxes.map(m => ({
        id: String(m.uniqueid),
        ext: m.mailbox,
        name: m.fullname || m.mailbox,
        type: (String(m.Type || '').toLowerCase() === 'company') ? 'company' : 'private',
        raw: m
      }))
    },

    // normalized message list for UI
    uiMessages(state) {
      return state.messages.map(m => {
        const { fromName, fromNumber } = parseCallerId(m.callerid)
        const iso = epochToIso(m.origtime)
        return {
          id: String(m.id),
          fromName,
          fromNumber,
          dateIso: iso,
          durationSec: Number(m.duration || 0),
          isNew: String(m.flag || '') !== 'read',
          flagged: false, // you can map later if you introduce a separate flag value
          flag: m.flag || '',
          mailboxuser: m.mailboxuser,
          mediaUrl: state.audioUrls[String(m.id)] || '',
          raw: m
        }
      })
    }
  },

  mutations: {
    setMailboxesLoading(state, v) { state.loadingMailboxes = v },
    setMailboxesError(state, v) { state.mailboxesError = v || '' },
    setMailboxes(state, rows) { state.mailboxes = rows || [] },

    setCurrentMailboxId(state, id) { state.currentMailboxId = id },

    setMessagesLoading(state, v) { state.loadingMessages = v },
    setMessagesError(state, v) { state.messagesError = v || '' },
    setMessages(state, rows) { state.messages = rows || [] },
    setMessagesPagination(state, p) {
      state.messagesPagination = {
        current_page: Number(p?.current_page || 1),
        last_page: Number(p?.last_page || 1),
        per_page: Number(p?.per_page || 50),
        total: Number(p?.total || 0)
      }
    },

    setMsgQuery(state, v) { state.msgQuery = v || '' },
    setMsgStatus(state, v) { state.msgStatus = v || '' },
    setMsgSort(state, v) { state.msgSort = v || '-id' },

    setAudioUrl(state, { id, url }) {
      state.audioUrls = { ...state.audioUrls, [String(id)]: url }
    },
    clearAudioUrl(state, id) {
      const key = String(id)
      if (!state.audioUrls[key]) return
      const next = { ...state.audioUrls }
      delete next[key]
      state.audioUrls = next
    },

    patchMessageFlag(state, { id, flag }) {
      const key = String(id)
      const idx = state.messages.findIndex(m => String(m.id) === key)
      if (idx >= 0) state.messages[idx].flag = flag
    },

    removeMessage(state, id) {
      const key = String(id)
      state.messages = state.messages.filter(m => String(m.id) !== key)
    },

    SET_USER_UNREAD(state, count) {
      state.userUnreadCount = Number(count) || 0
    }
  },

  actions: {
    async fetchUserUnreadCount({ commit }) {
      try {
        const res = await VoicemailService.userUnreadCount()
        commit('SET_USER_UNREAD', res?.data?.unread ?? 0)
      } catch (e) {
        commit('SET_USER_UNREAD', 0)
      }
    },

    async fetchMailboxes({ commit, dispatch, state }, params = {}) {
      commit('setMailboxesLoading', true)
      commit('setMailboxesError', '')
      try {
        const res = await VoicemailService.listMailboxes(params)
        const rows = safeArray(res?.data?.data ?? res?.data)
        commit('setMailboxes', rows)

        // auto select first mailbox if none selected
        if (!state.currentMailboxId && rows.length) {
          commit('setCurrentMailboxId', String(rows[0].uniqueid))
          await dispatch('fetchMessages')
        }
      } catch (e) {
        commit('setMailboxesError', e?.response?.data?.message || e?.message || 'Failed to load mailboxes')
      } finally {
        commit('setMailboxesLoading', false)
      }
    },

    async selectMailbox({ commit, dispatch }, mailboxUniqueId) {
      commit('setCurrentMailboxId', String(mailboxUniqueId))
      await dispatch('fetchMessages', { page: 1 })
    },

    async fetchMessages({ commit, getters, state }, { page } = {}) {
      const mb = getters.currentMailbox
      if (!mb) return

      commit('setMessagesLoading', true)
      commit('setMessagesError', '')

      try {
        const res = await VoicemailService.listMessages({
          mailboxuser: mb.mailbox,
          q: state.msgQuery || undefined,
          status: state.msgStatus || undefined,
          sort: state.msgSort || undefined,
          per_page: state.messagesPagination.per_page,
          page: page || state.messagesPagination.current_page
        })

        // Laravel paginator response
        const data = res?.data
        commit('setMessages', safeArray(data?.data))
        commit('setMessagesPagination', data)
      } catch (e) {
        commit('setMessagesError', e?.response?.data?.message || e?.message || 'Failed to load messages')
      } finally {
        commit('setMessagesLoading', false)
      }
    },

    async ensureAudioUrl({ commit, state }, messageId) {
      const id = String(messageId)
      if (state.audioUrls[id]) return state.audioUrls[id]

      const res = await VoicemailService.getRecordingBlob(id)
      const blob = res?.data
      const url = URL.createObjectURL(blob)
      commit('setAudioUrl', { id, url })
      return url
    },

    async markRead({ commit }, messageId) {
      const id = String(messageId)
      await VoicemailService.updateMessage(id, { flag: 'read' })
      commit('patchMessageFlag', { id, flag: 'read' })
    },

    async markUnread({ commit }, messageId) {
      const id = String(messageId)
      await VoicemailService.updateMessage(id, { flag: '' })
      commit('patchMessageFlag', { id, flag: '' })
    },

    async deleteMessage({ commit }, messageId) {
      const id = String(messageId)
      await VoicemailService.deleteMessage(id)
      commit('removeMessage', id)
      commit('clearAudioUrl', id)
    },

    async bulkAction({ dispatch }, { ids = [], action, flag } = {}) {
      if (!ids.length) return
      await VoicemailService.bulk({ ids: ids.map(Number), action, flag })
      // refresh to keep pagination consistent
      await dispatch('fetchMessages')
    },

    async createMailbox({ dispatch }, payload) {
      await VoicemailService.createMailbox(payload)
      await dispatch('fetchMailboxes')
    },

    async updateMailbox({ dispatch }, { id, payload }) {
      await VoicemailService.updateMailbox(id, payload)
      await dispatch('fetchMailboxes')
    },

    async deleteMailbox({ dispatch }, id) {
      await VoicemailService.deleteMailbox(id)
      await dispatch('fetchMailboxes')
    }
  }
}

export default voicemail
